import type { ActivitySegment } from '../util';

import { css, html } from 'react-strict-dom';
import { Color } from 'src/constants';
import { color } from 'src/constants/color.css';

import { FULL_DAY_MINUTES } from '../util';

type Props = {
  segments: ReadonlyArray<ActivitySegment>;
  /** Drawn on the end, still growing, when a timer is on the clock. */
  runningMinutes: number | null;
  runningCompanyId: string | null;
  colors: Record<string, string>;
};

/**
 * A day laid out left to right, one block per piece of work.  The track is a
 * fixed eight hours wide whatever the row holds, so rows compare to each other
 * rather than each filling itself; a longer day fills it and stops there.
 */
export default function ActivityBar({
  segments,
  runningMinutes,
  runningCompanyId,
  colors,
}: Props) {
  const segmentColor = (companyId: string | null) =>
    (companyId && colors[companyId]) || Color.neutral40;

  return (
    <html.div style={styles.track}>
      {segments.map((segment) => (
        <html.span
          key={segment.key}
          style={[
            styles.segment,
            styles.width(segment.minutes),
            styles.fill(segmentColor(segment.companyId)),
          ]}
        />
      ))}

      {runningMinutes !== null && (
        <html.span
          style={[
            styles.segment,
            styles.width(runningMinutes),
            styles.running(segmentColor(runningCompanyId)),
          ]}
        />
      )}
    </html.div>
  );
}

const styles = css.create({
  track: {
    display: 'flex',
    gap: 2,
    height: 10,
    borderRadius: 3,
    backgroundColor: color.neutral10,
    // A day past the full eight hours fills the track rather than running off
    // the end of the row.
    overflow: 'hidden',
  },
  segment: {
    height: 10,
    borderRadius: 3,
    flexShrink: 0,
  },
  width: (minutes: number) => ({
    // Anything logged at all stays visible, however short.
    width: `max(4px, ${(minutes / FULL_DAY_MINUTES) * 100}%)`,
  }),
  fill: (backgroundColor: string) => ({ backgroundColor }),
  running: (borderColor: string) => ({
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor,
    opacity: 0.7,
  }),
});
